/**
 * Confidence marks — the most important element in the system, designed first (Design System §05).
 * The underline IS the mark: dotted = inferred, single = stated, double green = verified, dotted
 * ochre = indication (a whole-document state, always a range). It marks the figure, never the row.
 */
import { MARK_CLASS, type MarkName } from "@oc/design-tokens";
import { CONFIDENCE_MARKS } from "@oc/copy";
import { h, cx, type ReactNode } from "./h.ts";

export type Confidence = 1 | 2 | 3;
export const markForConfidence = (c: Confidence): MarkName => (["inferred", "stated", "verified"] as const)[c - 1];
/** The margin word beside a figure — from packages/copy so the report and the console never disagree. */
export const confidenceWord = (c: Confidence): string => markForConfidence(c);

export interface MarkProps {
  /** The exact printed text — formatted upstream (packages/copy), never here. */
  text: string;
  mark: MarkName;
  className?: string;
  title?: string;
}

/** A figure with its mark. `text` is what the sidecar recorded; this component adds nothing to it. */
export function Mark({ text, mark, className, title }: MarkProps): ReactNode {
  // The hover text is the approved explanation of the mark (packages/copy), so a reader who does not
  // know the underline system learns it from the figure itself.
  return h("span", { className: cx(MARK_CLASS[mark], "oc-tnum", className), title: title ?? `${mark}: ${CONFIDENCE_MARKS[mark]}` }, text);
}

/** Unknown is a dash, never a zero (Design System §06). */
export function Dash(): ReactNode {
  return h("span", { className: "oc-dash", "aria-label": "unknown" }, "—");
}

/** A figure that may be absent: a Mark when there is text, a dash when there is not. */
export function Figure({ text, confidence, indication = false }: { text: string | null | undefined; confidence?: Confidence; indication?: boolean }): ReactNode {
  if (text === null || text === undefined || text === "") return h(Dash);
  return h(Mark, { text, mark: indication ? "indication" : markForConfidence(confidence ?? 2) });
}

/** The hero range on a public page — pencil, dotted, always a range (Design System §05, §16). */
export function HeroRange({ text }: { text: string }): ReactNode {
  return h("div", { className: "oc-hero-range oc-mi" }, text);
}

/** The single hero figure on a report or Review page — ink, the mark at hero scale. */
export function HeroFigure({ text, mark = "stated" }: { text: string; mark?: MarkName }): ReactNode {
  return h("div", { className: cx("oc-hero-figure", MARK_CLASS[mark]) }, text);
}

/** The legend a page shows once: which underline means what. */
export function MarkLegend({ examples }: { examples: { inferred: string; stated: string; verified: string } }): ReactNode {
  return h("div", { className: "oc-rows", role: "list" },
    legendRow("inferred", examples.inferred, "inferred by us"),
    legendRow("stated", examples.stated, "as stated to us"),
    legendRow("verified", examples.verified, "seen in a document"),
  );
}
function legendRow(mark: MarkName, example: string, meaning: string): ReactNode {
  return h("div", { className: "oc-row", role: "listitem", style: { gridTemplateColumns: "auto minmax(0,1fr)" } },
    h(Mark, { text: example, mark }), h("span", { className: "oc-secondary" }, meaning));
}
